/* src/views/logements/livretPartage.js — L'ORCHESTRATION DU LIVRET partagée
   entre les onglets Instructions et Livret de la fiche V2 (lot 4).

   Les handlers sont PORTÉS FIDÈLEMENT de LivretModulesEditor.jsx (loadData,
   module save/delete/toggle, reorder, prefill, config save, publish, passes
   de traduction FILL/OVERWRITE — invariants pessimist B7 conservés via les
   helpers importés). Les éditeurs lourds (module, alentours, assistance,
   copy-from, catégorie) restent les composants EXISTANTS, montés tels quels
   par les contrôleurs. */

/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../supabase'
import { autoTranslateEmptyLangs, retranslateAllLangs } from '../../lib/livret-autotranslate'
import { buildLangPatch, langChanged } from '../../tabs/logements/LivretModulesEditor'
import AlentoursAdminEditor from '../../tabs/logements/AlentoursAdminEditor'
import AssistanceAdminEditor from '../../tabs/logements/AssistanceAdminEditor'
import Feuille from '../accueil/Feuille'
import { lireAvecCache, invaliderCache } from './ficheCache'

export async function enTetesAuth() {
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token
  return token
    ? { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
    : { 'Content-Type': 'application/json' }
}

export function joursDepuis(iso) {
  const t = Date.parse(iso)
  if (!Number.isFinite(t)) return 0
  return Math.max(0, Math.round((Date.now() - t) / 86400000))
}

/* ── le chargement livret_admin_data + sections auto ──────────────── */
export function useLivretAdminData(logementId) {
  const [etat, setEtat] = useState('loading') // loading | no_config | ready | error
  const [erreur, setErreur] = useState(null)
  const [config, setConfig] = useState(null)
  const [modules, setModules] = useState([])
  const [faqs, setFaqs] = useState([])
  const [categories, setCategories] = useState([])
  const [modulePhotos, setModulePhotos] = useState({})
  const [sectionsAuto, setSectionsAuto] = useState(null) // { assistance, alentours }

  // Deux chemins de lecture (décision Dimitri 2026-08-28, appels en série) :
  // charger() passe par le CACHE partagé (Instructions et Livret montent le
  // même monde — un seul appel en vol, TTL court) ; recharger() INVALIDE puis
  // relit (toute mutation passe par lui — un cache qui survit à une écriture
  // est un menteur).
  const appliquer = useCallback((json, sections) => {
    setConfig(json.config || null)
    setModules(json.modules || [])
    setFaqs(json.faqs || [])
    setCategories(Array.isArray(json.categories) ? json.categories : [])
    setModulePhotos(json.module_photos || {})
    setEtat(json.config ? 'ready' : 'no_config')
    setErreur(null)
    if (sections) {
      const byType = {}
      for (const r of sections.sections || sections.rows || []) byType[r.section_type] = r
      setSectionsAuto(byType)
    }
  }, [])

  const charger = useCallback(async () => {
    if (!logementId) return
    try {
      const json = await lireAvecCache(`livret:${logementId}`, 60000, async () => {
        const headers = await enTetesAuth()
        const resp = await fetch(
          `/api/beds24-mapping?action=livret_admin_data&logement_id=${encodeURIComponent(logementId)}`,
          { method: 'GET', headers })
        const j = await resp.json()
        if (!resp.ok) throw new Error(j.message || j.error || `HTTP ${resp.status}`)
        return j
      })
      let sections = null
      try {
        sections = await lireAvecCache(`livret-sections:${logementId}`, 60000, async () => {
          const headers = await enTetesAuth()
          const resp = await fetch(
            `/api/beds24-mapping?action=livret_sections_auto_get&logement_id=${encodeURIComponent(logementId)}`,
            { method: 'GET', headers })
          const j = await resp.json()
          if (!resp.ok) throw new Error(j.message || j.error || `HTTP ${resp.status}`)
          return j
        })
      } catch (e) {
        console.error('[livret] sections auto', e?.message || e)
      }
      appliquer(json, sections)
    } catch (e) {
      setErreur(e.message || String(e))
      setEtat('error')
    }
  }, [logementId, appliquer])

  const recharger = useCallback(async () => {
    invaliderCache(`livret:${logementId}`)
    invaliderCache(`livret-sections:${logementId}`)
    await charger()
  }, [logementId, charger])

  useEffect(() => {
    setEtat('loading'); setConfig(null); setModules([]); setFaqs([])
    setCategories([]); setSectionsAuto(null)
    charger()
  }, [charger])

  const appel = useCallback(async (action, method, body) => {
    const headers = await enTetesAuth()
    const resp = await fetch(`/api/beds24-mapping?action=${action}`, {
      method, headers, body: JSON.stringify({ action, ...body }) })
    const json = await resp.json().catch(() => ({}))
    if (!resp.ok) throw new Error(json.message || json.error || `HTTP ${resp.status}`)
    return json
  }, [])

  const configSave = useCallback(async (patch) => {
    await appel('livret_config_upsert', 'PATCH', patch)
    await recharger()
  }, [appel, recharger])

  const setPublished = useCallback(async (published) => {
    await configSave({ logement_id: logementId, published })
  }, [configSave, logementId])

  const creerLivret = useCallback(async (titreFr) => {
    await appel('livret_config_upsert', 'POST', {
      logement_id: logementId, title: { fr: titreFr } })
    await recharger()
  }, [appel, recharger, logementId])

  const moduleToggle = useCallback(async (id, enabled) => {
    await appel('livret_module_upsert', 'PATCH', { id, enabled })
    await recharger()
  }, [appel, recharger])

  const moduleSave = useCallback(async (payload, moduleId) => {
    await appel('livret_module_upsert', moduleId ? 'PATCH' : 'POST',
      moduleId ? { id: moduleId, ...payload } : payload)
    await recharger()
  }, [appel, recharger])

  const moduleDelete = useCallback(async (mod) => {
    const title = mod?.title?.fr || mod?.title?.en || '(sans titre)'
    if (!window.confirm(`Supprimer définitivement le module "${title}" ?\n\nCette action est irréversible.`)) return false
    await appel('livret_module_upsert', 'DELETE', { id: mod.id })
    await recharger()
    return true
  }, [appel, recharger])

  const modulesReorder = useCallback(async (category, orderedIds) => {
    await appel('livret_module_upsert', 'PATCH', {
      action_sub: 'reorder', category, ordered_ids: orderedIds, logement_id: logementId })
    await recharger()
  }, [appel, recharger, logementId])

  const prefillAppareils = useCallback(async () => {
    await appel('livret_prefill_appareils', 'POST', { logement_id: logementId })
    await recharger()
  }, [appel, recharger, logementId])

  const creerCategorie = useCallback(async ({ slug, label_fr, label_en, label_es }) => {
    await appel('livret_category_create', 'POST', {
      logement_id: logementId, slug, label_fr, label_en, label_es })
    await recharger()
  }, [appel, recharger, logementId])

  /* ── passes de traduction (portées de runTranslatePass, M7) ──────
     FILL : langues vides seulement, throw au 1er échec.
     OVERWRITE : écrasement confirmé, continue malgré les échecs. */
  const passeTraduction = useCallback(async ({ overwrite }) => {
    const translateFn = overwrite ? retranslateAllLangs : autoTranslateEmptyLangs
    const continueOnError = !!overwrite
    let translated = 0; let complete = 0; let failures = 0
    const headers = await enTetesAuth()
    const hasFailedLocale = (...trs) => trs.some((tr) => (tr.failed?.length || 0) > 0)

    for (const m of modules) {
      try {
        const [trTitle, trContent] = await Promise.all([
          translateFn({ fr: m.title?.fr, en: m.title?.en, es: m.title?.es, context: 'title' }),
          translateFn({ fr: m.content?.fr, en: m.content?.en, es: m.content?.es, context: 'content' }),
        ])
        const itemFailed = hasFailedLocale(trTitle, trContent)
        const tCh = langChanged(m.title, trTitle)
        const cCh = langChanged(m.content, trContent)
        if (!tCh && !cCh) { itemFailed ? failures++ : complete++; continue }
        const body = { action: 'livret_module_upsert', id: m.id }
        if (tCh) body.title = buildLangPatch(m.title, trTitle)
        if (cCh) body.content = buildLangPatch(m.content, trContent)
        const r = await fetch('/api/beds24-mapping?action=livret_module_upsert',
          { method: 'PATCH', headers, body: JSON.stringify(body) })
        if (!r.ok) {
          const j = await r.json().catch(() => ({}))
          throw new Error(j.message || j.error || `module ${m.id} HTTP ${r.status}`)
        }
        itemFailed ? failures++ : translated++
      } catch (e) {
        if (!continueOnError) throw e
        console.error('passeTraduction — module en échec :', e)
        failures++
      }
    }
    for (const f of faqs) {
      try {
        const [trQ, trA] = await Promise.all([
          translateFn({ fr: f.question?.fr, en: f.question?.en, es: f.question?.es, context: 'content' }),
          translateFn({ fr: f.answer?.fr, en: f.answer?.en, es: f.answer?.es, context: 'content' }),
        ])
        const itemFailed = hasFailedLocale(trQ, trA)
        const qCh = langChanged(f.question, trQ)
        const aCh = langChanged(f.answer, trA)
        if (!qCh && !aCh) { itemFailed ? failures++ : complete++; continue }
        // PATCH partiel : jamais de JSONB vide (garde miroir welcome_subtitle).
        const body = { action: 'livret_faq_upsert', id: f.id }
        if (qCh) {
          const q = buildLangPatch(f.question, trQ)
          if (Object.keys(q).length > 0) body.question = q
        }
        if (aCh) {
          const a = buildLangPatch(f.answer, trA)
          if (Object.keys(a).length > 0) body.answer = a
        }
        if (!body.question && !body.answer) { itemFailed ? failures++ : complete++; continue }
        const r = await fetch('/api/beds24-mapping?action=livret_faq_upsert',
          { method: 'PATCH', headers, body: JSON.stringify(body) })
        if (!r.ok) {
          const j = await r.json().catch(() => ({}))
          throw new Error(j.message || j.error || `faq ${f.id} HTTP ${r.status}`)
        }
        itemFailed ? failures++ : translated++
      } catch (e) {
        if (!continueOnError) throw e
        console.error('passeTraduction — FAQ en échec :', e)
        failures++
      }
    }
    if (config) {
      try {
        const [trT, trW] = await Promise.all([
          translateFn({ fr: config.title?.fr, en: config.title?.en, es: config.title?.es, context: 'title' }),
          translateFn({ fr: config.welcome_subtitle?.fr, en: config.welcome_subtitle?.en, es: config.welcome_subtitle?.es, context: 'content' }),
        ])
        const itemFailed = hasFailedLocale(trT, trW)
        const tCh = langChanged(config.title, trT)
        const wCh = langChanged(config.welcome_subtitle, trW)
        if (!tCh && !wCh) { itemFailed ? failures++ : complete++ } else {
          const body = { action: 'livret_config_upsert', logement_id: logementId }
          if (tCh) body.title = buildLangPatch(config.title, trT)
          if (wCh) {
            const w = buildLangPatch(config.welcome_subtitle, trW)
            if (Object.keys(w).length > 0) body.welcome_subtitle = w
          }
          if (body.title || body.welcome_subtitle) {
            const r = await fetch('/api/beds24-mapping?action=livret_config_upsert',
              { method: 'PATCH', headers, body: JSON.stringify(body) })
            if (!r.ok) {
              const j = await r.json().catch(() => ({}))
              throw new Error(j.message || j.error || `config HTTP ${r.status}`)
            }
            itemFailed ? failures++ : translated++
          } else { itemFailed ? failures++ : complete++ }
        }
      } catch (e) {
        if (!continueOnError) throw e
        console.error('passeTraduction — config en échec :', e)
        failures++
      }
    }
    await recharger()
    return { translated, complete, failures }
  }, [modules, faqs, config, logementId, recharger])

  // Dérivés partagés par les aperçus.
  const modulesAffiches = modules.filter((m) => m.enabled)
  const guideVide = !modules.some((m) => m.category === 'guide_appareils' && m.enabled)
  const nbAlentours = sectionsAuto?.alentours
  const alentoursLigne = nbAlentours && nbAlentours.last_generated_at
    ? `Sept secteurs · revu il y a ${joursDepuis(nbAlentours.last_generated_at)} jours`
    : null
  const wifiNetworks = config === null && etat === 'loading'
    ? undefined
    : (Array.isArray(config?.wifi_networks) ? config.wifi_networks : [])

  return {
    etat, erreur, config, modules, faqs, categories, modulePhotos, sectionsAuto,
    recharger, configSave, setPublished, creerLivret,
    moduleToggle, moduleSave, moduleDelete, modulesReorder,
    prefillAppareils, creerCategorie, passeTraduction,
    modulesAffiches, guideVide, alentoursLigne, wifiNetworks,
  }
}

/* ── la feuille « Dépublier le livret » ───────────────────────────── */
export function FeuilleDepublierLivret({ onDepublier, onFermer }) {
  const [enCours, setEnCours] = useState(false)
  const [erreur, setErreur] = useState(null)
  return (
    <Feuille titre="Dépublier le livret" testId="feuille-depublier"
      onFermer={onFermer}
      enfants={(
        <>
          <div className="warn">Les voyageurs qui ont déjà le lien verront
            « livret en cours de préparation » à leur prochaine ouverture.{' '}
            <b>Aucun QR code n'est cassé</b>, et republier remet le contenu.</div>
          {erreur && (
            <div className="info" style={{ borderColor: '#e8c4b8',
              background: '#fbeee9', color: '#8a3b28' }}>{erreur}</div>
          )}
          <div className="act">
            <button className="bt bt-p" data-testid="depublier-confirmer"
              disabled={enCours}
              onClick={async () => {
                setEnCours(true); setErreur(null)
                try { await onDepublier() } catch (e) {
                  setErreur(e.message || String(e)); setEnCours(false)
                }
              }}>{enCours ? 'Dépublication…' : 'Dépublier'}</button>
            <button className="bt bt-s" onClick={onFermer}>Annuler</button>
          </div>
        </>
      )} />
  )
}

/* ── la feuille « Revoir » d'une section automatique ──────────────────
   Trois sorties du proto : Refaire la section (régénération IA — pour
   l'assistance, elle écrase les 3 contacts IA, y compris édités ; pour les
   alentours, les choix manuels sont préservés — textes honnêtes par type),
   Modifier à la main (ouvre l'éditeur EXISTANT complet), Annuler. */
export function FeuilleRefaireSection({ type, logementId, sectionRow, onFini, onFermer }) {
  const [editerMain, setEditerMain] = useState(false)
  const [enCours, setEnCours] = useState(false)
  const [erreur, setErreur] = useState(null)
  const titre = type === 'assistance' ? 'Assistance et urgences' : 'Aux alentours'

  if (editerMain) {
    const Editeur = type === 'assistance' ? AssistanceAdminEditor : AlentoursAdminEditor
    return (
      <Editeur logement_id={logementId} row={sectionRow}
        onReload={() => { onFini() }} />
    )
  }

  const refaire = async () => {
    setEnCours(true); setErreur(null)
    try {
      const headers = await enTetesAuth()
      const resp = await fetch('/api/beds24-mapping?action=livret_sections_auto_generate', {
        method: 'POST', headers,
        body: JSON.stringify({ action: 'livret_sections_auto_generate',
          logement_id: logementId, section_type: type }) })
      const json = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(json.message || json.error || `HTTP ${resp.status}`)
      onFini()
    } catch (e) {
      setErreur(e.message || String(e)); setEnCours(false)
    }
  }

  const supprimer = async () => {
    if (!window.confirm(`Supprimer « ${titre} » ? Le bloc disparaît du livret ; « Refaire la section » le reconstruira depuis l'adresse.`)) return
    setEnCours(true); setErreur(null)
    try {
      const headers = await enTetesAuth()
      const resp = await fetch('/api/beds24-mapping?action=livret_sections_auto_delete', {
        method: 'POST', headers,
        body: JSON.stringify({ action: 'livret_sections_auto_delete',
          logement_id: logementId, section_type: type }) })
      const json = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(json.message || json.error || `HTTP ${resp.status}`)
      onFini()
    } catch (e) {
      setErreur(e.message || String(e)); setEnCours(false)
    }
  }

  const jours = sectionRow?.last_generated_at ? joursDepuis(sectionRow.last_generated_at) : null
  return (
    <Feuille titre={`Revoir « ${titre} »`} testId="feuille-refaire-section"
      onFermer={onFermer}
      enfants={(
        <>
          {type === 'assistance' ? (
            <div className="dang">Refaire écrase les trois contacts trouvés par
              l'IA, <b>y compris vos corrections</b>. Les contacts que vous avez
              ajoutés vous-même sont préservés.</div>
          ) : (
            <div className="info">Quinze nouvelles suggestions par secteur.
              Vos choix (épinglés, exclus, ajoutés) sont <b>préservés</b>.</div>
          )}
          <div className="carte" style={{ margin: '0' }}>
            <p style={{ fontSize: '11.5px', margin: '0', color: 'var(--gris)',
              lineHeight: '1.6' }}>Elle se reconstruit à partir de l'adresse du
              logement : commerces, transports et services les plus proches.</p>
            <p className="note">{jours != null
              ? `Dernière construction il y a ${jours} jour${jours > 1 ? 's' : ''}.`
              : 'Jamais construite pour l’instant.'}</p>
          </div>
          {erreur && (
            <div className="info" style={{ borderColor: '#e8c4b8',
              background: '#fbeee9', color: '#8a3b28', marginTop: '8px' }}>{erreur}</div>
          )}
          <div className="act">
            <button className="bt bt-p" data-testid="refaire-section"
              disabled={enCours} onClick={refaire}>
              {enCours ? 'Construction…' : 'Refaire la section'}</button>
            <button className="bt bt-s" data-testid="modifier-main"
              onClick={() => setEditerMain(true)}>Modifier à la main</button>
            {/* Remise sur décision Dimitri (2026-08-28) : la suppression
                existait, elle ne se perd pas sur une lecture. */}
            {sectionRow && (
              <button className="bt bt-s" data-testid="supprimer-section"
                style={{ color: '#b8412b', borderColor: '#e8c4b8' }}
                disabled={enCours} onClick={supprimer}>Supprimer la section</button>
            )}
            <button className="bt bt-s" onClick={onFermer}>Annuler</button>
          </div>
        </>
      )} />
  )
}
